<template>
  <div :class="['ai-page', { 'locale-ja': localeValue === 'ja' }]">
    <section class="hero">
      <img :src="getImageUrl(pr.heroImage)" :alt="pr.heroTitle" class="hero-bg">
      <div class="hero-mask"></div>
      <div class="inner hero-inner">
        <SIBreadcrumb
          :breadcrumb="pr.common.breadcrumb"
          :locale="localeValue"
          :show-products="false"
          :current="pr.pageTitle || pr.heroTitle"
        />
        <h1>{{ pr.pageTitle || pr.heroTitle }}</h1>
        <p class="hero-subtitle">{{ pr.heroSubtitle }}</p>
        <p class="hero-tagline">{{ pr.heroTagline }}</p>
      </div>
    </section>

    <section class="intro">
      <div class="inner narrow">
        <p
          v-viewportChanged="showView"
          class="hidden-element"
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="block light-bg">
      <div class="inner">
        <SISectionTitle :title="pr.capTitle" :subtitle="pr.capSubtitle" />
        <div v-viewportChanged="showView" class="hidden-element card-grid three">
          <article v-for="item in pr.capabilities" :key="item.title" class="card">
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title">
            </div>
            <div class="title-content">
              <h3>{{ item.title }}</h3>
              <span v-if="item.titleExtra" class="title-extra">{{ item.titleExtra }}</span>
            </div>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="block white-bg">
      <div class="inner">
        <SISectionTitle :title="pr.common.scenarios" :subtitle="pr.common.scenariosEn" />
        <div v-viewportChanged="showView" class="hidden-element card-grid two">
          <article v-for="item in pr.industries" :key="item.title" class="card industry-card">
            <div class="industry-head">
              <div class="icon-wrap circle small">
                <img :src="getImageUrl(item.icon)" :alt="item.title">
              </div>
              <h3>{{ item.title }}</h3>
            </div>
            <ul class="case-list">
              <li v-for="line in item.cases" :key="line">{{ line }}</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <section class="block light-bg method-block">
      <div class="inner">
        <SISectionTitle :title="pr.methodTitle" :subtitle="pr.methodTitleEn" />
        <div v-viewportChanged="showView" class="hidden-element method-row">
          <article v-for="(step, index) in pr.methodologySteps" :key="step.num" class="method-step">
            <div v-if="index > 0" class="link-line"></div>
            <div class="num">{{ step.num }}</div>
            <h4>{{ step.title }}</h4>
            <p>{{ step.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="block dark-bg">
      <div class="inner">
        <SISectionTitle :title="pr.common.benefits" :subtitle="pr.common.benefitsEn" light />
        <div v-viewportChanged="showView" class="hidden-element card-grid four">
          <article v-for="item in pr.benefits" :key="item.title" class="benefit-card">
            <div class="benefit-number">
              <span
                v-for="(part, idx) in formatBenefitNumber(item.number)"
                :key="`${item.title}-num-${idx}`"
                :class="['num-part', part.type]"
              >
                {{ part.text }}
              </span>
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="cta">
      <div class="inner cta-inner">
        <SISectionTitle :title="pr.common.cta" />
        <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">
          <span v-viewportChanged="showView" class="hidden-element">{{ contactText }}</span>
        </BaseButton>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import usePageResource from '@/composables/usePageResource';
import useRenderData from '@/composables/useRenderData';
import BaseButton from '@/components/common/BaseButton.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';
import formatBenefitNumber from '@/utils/formatBenefitNumber';

export default {
  name: 'ai-page',
  components: {
    BaseButton,
    SIBreadcrumb,
    SISectionTitle,
  },
  setup() {
    const { pr, getImageUrl } = useRenderData('ai');
    const { pr: works } = usePageResource('works');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactText = computed(() => works.value.contact);
    const contactLink = computed(() => ({
      name: 'contact-us',
      params: {
        lang: localeValue.value || undefined,
      },
    }));

    return {
      pr,
      getImageUrl,
      localeValue,
      contactText,
      contactLink,
      formatBenefitNumber,
    };
  },
};
</script>

<style lang="scss" scoped>
.ai-page {
  background: #ffffff;
}

.ai-page.locale-ja {
  h1,
  h2,
  h3,
  h4,
  p,
  li,
  span,
  strong,
  td,
  th {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

.inner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.narrow {
  max-width: 800px;
}

.hero {
  position: relative;
  min-height: 420px;
  display: flex;
  align-items: flex-end;

  .hero-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .hero-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.64);
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    padding-bottom: 48px;

    h1 {
      margin: 24px 0 0;
      font-size: 32px;
      line-height: 44px;
      color: #ffffff;
      font-weight: 700;
    }

    .hero-subtitle {
      margin: 8px 0 0;
      font-size: 16px;
      line-height: 24px;
      color: #a7aeb1;
    }

    .hero-tagline {
      margin: 12px 0 0;
      max-width: 600px;
      font-size: 16px;
      line-height: 28px;
      color: rgba(255, 255, 255, 0.8);
    }
  }
}

.intro {
  padding: 64px 0;

  p {
    margin: 0 0 16px;
    font-size: 16px;
    line-height: 30px;
    color: #222222;
    text-align: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.block {
  padding: 82px 0;

  &.light-bg {
    background: #f7f9fa;
  }

  &.white-bg {
    background: #ffffff;
  }

  &.dark-bg {
    background: #222222;
  }
}

.card-grid {
  display: grid;
  gap: 24px;

  &.three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &.two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &.four {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.card {
  border: 1px solid #e4e9eb;
  background: #ffffff;
  padding: 24px;

  .icon-wrap {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }

    &.circle {
      width: 56px;
      height: 56px;
      border-radius: 9999px;
      background: #f1f3f4;

      &.small {
        width: 40px;
        height: 40px;

        img {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .title-content {
    display: flex;
    align-items: baseline;
    flex-wrap: nowrap;

    h3 {
      margin: 16px 0 8px;
      font-size: 18px;
      line-height: 27px;
      color: #212121;
      font-weight: 600;
    }

    .title-extra {
      font-weight: 400;
      font-size: 14px;
      line-height: 27px;
      color: #666666;
      white-space: nowrap;
    }
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 24px;
    color: #666666;
  }
}

.industry-card {
  transition:
    transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
    box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);
  }

  .industry-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    h3 {
      margin: 0;
    }
  }

  .case-list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 14px;
      margin-bottom: 8px;
      color: #666666;
      font-size: 14px;
      line-height: 22px;

      &:last-child {
        margin-bottom: 0;
      }

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 8px;
        width: 6px;
        height: 6px;
        border-radius: 9999px;
        background: #1565c0;
      }
    }
  }
}

.method-block {
  .method-row {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    align-items: stretch;
    gap: 0;
  }

  .method-step {
    position: relative;
    flex: 1;
    text-align: center;

    .link-line {
      position: absolute;
      top: 23px;
      left: -50%;
      width: 100%;
      height: 2px;
      background: #e4e9eb;
    }

    .num {
      position: relative;
      z-index: 1;
      margin: 0 auto;
      width: 48px;
      height: 48px;
      border-radius: 9999px;
      background: #212121;
      color: #ffffff;
      font-size: 14px;
      line-height: 48px;
      font-weight: 700;
    }

    h4 {
      margin: 16px 0 4px;
      color: #212121;
      font-size: 14px;
      line-height: 22px;
      font-weight: 600;
    }

    p {
      margin: 0;
      padding: 0 8px;
      color: #666666;
      font-size: 12px;
      line-height: 18px;
    }
  }
}

.benefit-card {
  background: #303233;
  padding: 24px;

  .benefit-number {
    color: #3784e6;
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;

    .num-part {
      display: inline-block;
    }

    .num-part.plus,
    .num-part.minus {
      margin-inline: 0.2em;
    }

    .num-part.minus {
      transform: translateY(-0.1em);
    }
    .num-part.plus {
      transform: translateY(-0.2em);
    }
  }

  h3 {
    margin: 12px 0 8px;
    color: #ffffff;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 14px;
    line-height: 22px;
  }
}

.cta {
  background: #f1f3f4;
  padding: 66px 0;

  .cta-inner {
    text-align: center;
  }
}

@media only screen and (max-width: 1024px) {
  .card-grid {
    &.three {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &.four {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

}

@media only screen and (max-width: 768px) {
  .inner {
    padding: 0 12px;
  }

  .hero {
    min-height: 340px;

    .hero-inner {
      padding-bottom: 28px;

      h1 {
        margin-top: 16px;
        font-size: 26px;
        line-height: 36px;
      }

      .hero-tagline {
        font-size: 14px;
        line-height: 24px;
      }
    }
  }

  .intro {
    padding: 42px 0;

    p {
      text-align: left;
      font-size: 14px;
      line-height: 24px;
    }
  }

  .block {
    padding: 56px 0;
  }

  .card-grid {
    &.three,
    &.two,
    &.four {
      grid-template-columns: 1fr;
      gap: 14px;
    }
  }

  .method-block {
    .method-row {
      flex-direction: column;
      gap: 16px;
    }

    .method-step {
      .link-line {
        display: none;
      }
    }
  }

  .cta {
    padding: 54px 0;
  }
}
</style>
